import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { ArrowButton } from 'components/ArrowButton';
import { Box } from 'components/Box';
import { AnchorButton } from 'components/Button/AnchorButton';
import { AnalyticsData } from 'components/Text/AnalyticsData';
import { Body } from 'components/Text/Body';
import { useHideDocumentScroll } from 'hooks/useHideDocumentScroll';
import { useOnWindowKeyDown } from 'hooks/useOnWindowKeyDown';
import { CmsApiLink } from 'modules/cms/api/types/CmsApiLink';
import { CmsButton } from 'modules/cms/components/Button';

import {
  CmsTileModalBackdrop,
  CmsTileModalContainer,
  CmsTileModalContent,
  CmsTileModalTwoColumns,
  CmsTileModalTwoColumnsContent,
  TileModalDesktopArrowContainer,
} from './CmsTileModal.styled';
import { CmsTileModalDesktopHeader } from './DesktopHeader/CmsTileModalDesktopHeader';
import { CmsTileModalLeftContent } from './LeftContent/CmsTileModalLeftContent';
import { CmsTileModalMobileHeader } from './MobileHeader/CmsTileModalMobileHeader';
import { CmsTileModalRightContent } from './RightContent/CmsTileModalRightContent';

type Props = {
  blockId: string;
  sectionIndex: number;
  heading: string;
  subHeading: string | null;
  description: string;
  caption?: string;
  imageData: ApiFilestackImageData;
  buttonData?: CmsApiLink;
  onClose: () => void;
  hasPrevious: boolean;
  onPrevious: () => void;
  hasNext: boolean;
  onNext: () => void;
  analyticsData: AnalyticsData;
};

export function CmsTileModal({
  blockId,
  sectionIndex,
  heading,
  subHeading,
  description,
  caption,
  imageData,
  buttonData,
  onClose,
  hasPrevious,
  onPrevious,
  hasNext,
  onNext,
  analyticsData,
}: Props) {
  useHideDocumentScroll();
  useOnWindowKeyDown(['Escape'], onClose);

  return (
    <>
      <CmsTileModalBackdrop role="button" onClick={onClose} />

      <CmsTileModalContainer>
        <CmsTileModalMobileHeader
          hasPrevious={hasPrevious}
          onPrevious={onPrevious}
          hasNext={hasNext}
          onNext={onNext}
          onClose={onClose}
        />

        <CmsTileModalDesktopHeader onClose={onClose} />

        <CmsTileModalContent>
          <TileModalDesktopArrowContainer>
            <ArrowButton
              direction="left"
              onClick={onPrevious}
              disabled={!hasPrevious}
            />
          </TileModalDesktopArrowContainer>

          <CmsTileModalTwoColumns>
            <CmsTileModalTwoColumnsContent>
              <CmsTileModalLeftContent
                imageData={imageData}
                caption={caption}
              />

              <CmsTileModalRightContent
                heading={heading}
                subHeading={subHeading}
              >
                <Body
                  analyticsData={analyticsData}
                  dangerouslySetInnerHTML={{ __html: description }}
                  styleAs="bodySmall"
                />

                {buttonData ? (
                  <Box mt={[24, null, 36]}>
                    <CmsButton
                      blockId={blockId}
                      sectionIndex={sectionIndex}
                      buttonData={buttonData}
                      eventProperties={{
                        button_text: buttonData.text,
                        ...analyticsData.eventProperties,
                      }}
                    >
                      <AnchorButton
                        variant="tertiary"
                        iconPosition="right"
                        iconName="arrow-right"
                        noPadding
                      />
                    </CmsButton>
                  </Box>
                ) : null}
              </CmsTileModalRightContent>
            </CmsTileModalTwoColumnsContent>
          </CmsTileModalTwoColumns>

          <TileModalDesktopArrowContainer>
            <ArrowButton
              direction="right"
              onClick={onNext}
              disabled={!hasNext}
            />
          </TileModalDesktopArrowContainer>
        </CmsTileModalContent>
      </CmsTileModalContainer>
    </>
  );
}
